import { useCallback, useEffect, useState } from 'react';
import type { Item, PlanResponse } from '../types';
import { api } from '../api';
import { useI18n } from '../i18n';
import { QuickCapture } from './QuickCapture';
import { ItemList } from './ItemList';
import { NextActions } from './NextActions';

export function Dashboard() {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<Item[]>([]);
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [loadingItems, setLoadingItems] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const { items } = await api.items({ limit: 10 });
      setItems(items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  async function generatePlan() {
    setGenerating(true);
    setError(null);
    try {
      const p = await api.plan('today', undefined, locale);
      setPlan(p);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  const today = new Date().toLocaleDateString(locale === 'ru' ? 'ru-RU' : undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('dashboard.title')}</h1>
          <div className="page-sub">{today}</div>
        </div>
        <button
          className="btn btn-primary"
          onClick={generatePlan}
          disabled={generating || items.length === 0}
        >
          {generating ? t('dashboard.generating') : t('dashboard.generate')}
        </button>
      </header>

      <div className="grid-2">
        <div className="stack">
          <section className="card">
            <h2 className="card-title">{t('dashboard.section.capture')}</h2>
            <QuickCapture onCaptured={loadItems} />
          </section>

          <section className="card">
            <h2 className="card-title">{t('dashboard.section.recent')}</h2>
            {loadingItems ? (
              <div className="empty">
                <span className="skeleton" />
              </div>
            ) : (
              <ItemList items={items.slice(0, 6)} emptyHint={t('dashboard.empty.capture')} />
            )}
          </section>
        </div>

        <section className="card">
          <h2 className="card-title">{t('dashboard.section.suggestions')}</h2>
          {error && <div className="error-banner">{error}</div>}
          {!plan && !generating && (
            <div className="empty">{t('dashboard.empty.noplan')}</div>
          )}
          {generating && <div className="empty">{t('dashboard.thinking')}</div>}
          {plan && <NextActions plan={plan} />}
        </section>
      </div>
    </>
  );
}
